import { Search, Plus, QrCode, RotateCw } from 'lucide-react'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { useEffect, useState, useRef } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import Select from 'react-select'
import { useDebounce } from 'use-debounce'
import Loader from '../../../components/atoms/Loader'
import { BadgeStatusSj } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import Pdf from '../../../components/Download/Pdf'
import ModalAddPallete from '../../../components/modal/pallete/ModalAddPallete'
import ModalDownloadQr from '../../../components/modal/pallete/ModalDownloadQr'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  backgroundColor: '#f8f9fa',
  color: '#333333',
  fontWeight: '700',
  borderBottom: '1px solid #e9ecef',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  border: '1px solid #e9ecef',
  borderRadius: '8px',
  padding: '0.6rem 1rem',
  fontSize: '0.9rem',
  backgroundColor: '#ffffff',
  transition: 'all 0.2s',
  height: '45px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const initialMeta = {
  per_page: 10,
  current_page: 1,
  total_row: 0,
  total_page: 0
}

const optionsIsAvailable = [
  { label: 'ALL', value: '' },
  { label: 'AVAILABLE', value: 'true' },
  { label: 'USED', value: 'false' }
]

const MasterPallete = () => {
  const [showModalAdd, setShowModalAdd] = useState(false)
  const [showDownloadQr, setShowDownloadQr] = useState(false)
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(false)
  const [pallettes, setPalletes] = useState([])

  const [filter, setFilter] = useState({
    warehouseId: null,
    search: '',
    isAvailable: optionsIsAvailable[0]
  })

  const [debouncedSearch] = useDebounce(filter.search, 1000)

  const [editId, setEditId] = useState({
    src: '',
    paletName: '',
    id: ''
  })

  const [meta, setMeta] = useState(initialMeta)
  const [checkedPalletId, setCheckedPalletId] = useState([])
  const { per_page, current_page } = meta
  const [showDownloadPdf, setShowDownloadPdf] = useState(false)

  const prevSearch = useRef(debouncedSearch)
  const prevWarehouse = useRef(filter.warehouseId)
  const prevStatus = useRef(filter.isAvailable)

  const abortControllerRef = useRef(null)

  useEffect(() => {
    const searchChanged = prevSearch.current !== debouncedSearch
    const warehouseChanged = prevWarehouse.current !== filter.warehouseId
    const statusChanged = prevStatus.current !== filter.isAvailable

    if (!searchChanged && !warehouseChanged && !statusChanged) {
      return
    }

    prevSearch.current = debouncedSearch
    prevWarehouse.current = filter.warehouseId
    prevStatus.current = filter.isAvailable

    if (meta.current_page !== 1) {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    } else {
      if (filter.warehouseId) {
        fetchPalletes()
      } else {
        setPalletes([])
        setMeta(initialMeta)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filter.warehouseId, filter.isAvailable])

  useEffect(() => {
    if (filter.warehouseId) {
      fetchPalletes()
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [per_page, current_page])

  const fetchWarehouse = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const formatted = data.map((d) => ({
        label: d.warehouse_name,
        value: d.id
      }))
      setWarehouses(formatted)
    } catch (err) {
      // ignore
    }
  }

  async function fetchPalletes() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      setLoading(true)
      const res = await api.get(`master/palets/`, {
        signal,
        params: {
          per_page,
          page: current_page,
          warehouse_id: filter.warehouseId?.value,
          q: debouncedSearch,
          is_available: filter.isAvailable?.value
        }
      })
      if (res.data.success) {
        const { metadata, data } = res.data
        setPalletes(data)
        setMeta(metadata)
      }
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  function handleDownloadQr(data) {
    setShowDownloadQr(true)
    setEditId({
      ...editId,
      src: data.src,
      paletName: data.paletName,
      id: data.id
    })
  }

  const handleCheckInput = (e) => {
    const { value, checked } = e.target
    if (value === 'all') {
      setCheckedPalletId([])
      if (checked) {
        const allId = pallettes.reduce((arr, cur) => {
          arr.push({
            id: cur.id,
            qr: cur.qr,
            palet_id: cur.palet_id
          })
          return arr
        }, [])
        setCheckedPalletId(allId)
      }
    } else {
      if (checked) {
        setCheckedPalletId([...checkedPalletId, JSON.parse(value)])
      } else {
        setCheckedPalletId(
          checkedPalletId.filter(
            (pallet) => pallet.id !== JSON.parse(value)?.id
          )
        )
      }
    }
  }

  const shardingItems = (propsDataValue) => {
    const data = JSON.parse(propsDataValue).reduce((acc, cur) => {
      acc.push({ id: cur.id, qr: cur.qr, name: cur.palet_id })
      return acc
    }, [])
    const shardedData = []

    for (let i = 0; i < data.length; i += 2) {
      shardedData.push(data.slice(i, i + 2))
    }
    return shardedData
  }

  const resetFilters = () => {
    setFilter({
      warehouseId: null,
      search: '',
      isAvailable: optionsIsAvailable[0]
    })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  useEffect(() => {
    fetchWarehouse()
  }, [])

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Palet</h3>
        </Col>
        <Col className="text-end">
          <Button
            className="btn btn-primary-orange shadow-sm rounded px-4"
            disabled={!filter.warehouseId}
            onClick={() => setShowModalAdd(true)}
          >
            <Plus size={16} className="me-2" />
            Add Pallet
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                name="warehouse"
                value={filter.warehouseId}
                onChange={(val) => setFilter({ ...filter, warehouseId: val })}
                options={warehouses}
                styles={SelectStyles}
                placeholder="Select Warehouse"
                isClearable
                isSearchable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Status</Form.Label>
              <Select
                name="status"
                value={filter.isAvailable}
                onChange={(val) => setFilter({ ...filter, isAvailable: val })}
                options={optionsIsAvailable}
                styles={SelectStyles}
                placeholder="Select Status"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search Name</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#ffffff',
                    borderRight: 'none',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    borderColor: '#e9ecef'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Pallet..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  disabled={!filter.warehouseId}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0'
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button
              variant="light"
              className="w-100 rounded shadow-sm"
              style={{
                height: '45px',
                borderColor: '#e9ecef',
                color: '#6c757d',
                fontWeight: '600'
              }}
              onClick={resetFilters}
            >
              <RotateCw size={16} className="me-2" />
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      <div className="d-flex justify-content-end mb-3 gap-2">
        {/* Print QR Buttons */}
        {!checkedPalletId.length && (
          <button
            className="btn btn-secondary opacity-50 rounded px-3"
            style={{ fontSize: '12px', height: '36px' }}
            disabled
          >
            <QrCode size={16} className="me-2" /> Print QR
          </button>
        )}
        {checkedPalletId.length > 0 && (
          <PDFDownloadLink
            document={
              <Pdf
                data={shardingItems(JSON.stringify(checkedPalletId))}
                typeDownload="pallet"
              />
            }
            fileName="print_qr_pallete.pdf"
            onClick={() => {
              setShowDownloadPdf(true)
              setTimeout(() => {
                setShowDownloadPdf(false)
              }, 1000)
            }}
          >
            {({ loading }) => (
              <button
                className={`btn ${
                  showDownloadPdf
                    ? 'btn-secondary opacity-50'
                    : 'btn-primary-orange'
                } rounded px-3`}
                style={{ height: '36px' }}
                disabled={showDownloadPdf}
              >
                <QrCode size={16} className="me-2" />{' '}
                {showDownloadPdf ? 'Downloading' : 'Print QR'}
              </button>
            )}
          </PDFDownloadLink>
        )}
      </div>

      <div className="row">
        <Col xs={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table hover responsive className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle" style={{ width: '5%' }}>
                    <input
                      className="staging-area form-check-input"
                      type="checkbox"
                      name="parent_palette"
                      value={'all'}
                      disabled={pallettes.length === 0 ? true : false}
                      checked={
                        checkedPalletId.length === pallettes.length &&
                        pallettes.length !== 0
                          ? true
                          : false
                      }
                      onChange={(e) => handleCheckInput(e)}
                    />
                  </th>
                  <th className="p-2 align-middle">Palet ID</th>
                  <th className="p-2 align-middle">Status</th>
                  <th className="p-2 align-middle">Location</th>
                  <th className="p-2 align-middle text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && !pallettes.length && (
                  <tr>
                    <td colSpan={5} className="text-center p-5 text-muted">
                      No data available
                    </td>
                  </tr>
                )}
                {!loading &&
                  pallettes.length > 0 &&
                  pallettes?.map((pallete) => (
                    <TbodyRow
                      data={pallete}
                      key={pallete.id}
                      checkedPalletId={checkedPalletId}
                      handleCheckInput={handleCheckInput}
                      onHandleDownloadQr={handleDownloadQr}
                    />
                  ))}
              </tbody>
            </Table>
            {pallettes.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handleOnChangeMeta={handleOnChangeMeta}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </Card>
        </Col>
      </div>
      <ModalAddPallete
        show={showModalAdd}
        onHide={() => setShowModalAdd(false)}
        data={filter.warehouseId?.value}
        refreshData={() => fetchPalletes()}
      />
      <ModalDownloadQr
        show={showDownloadQr}
        onHide={() => {
          setShowDownloadQr(false)
          setEditId({ ...editId, src: '', paletName: '', id: '' })
        }}
        paletData={editId}
      />
    </div>
  )
}

function TbodyRow({
  data,
  onHandleDownloadQr,
  checkedPalletId,
  handleCheckInput
}) {
  const dataValue = {
    src: data.qr,
    paletName: data.palet_id,
    id: data.id
  }
  return (
    <tr key={data.id}>
      <td className="p-2 align-middle ps-4">
        <input
          className="staging-area form-check-input"
          type="checkbox"
          name="palette"
          value={JSON.stringify({
            id: data.id,
            palet_id: data.palet_id,
            qr: data.qr
          })}
          checked={
            checkedPalletId.find((pallet) => pallet.id === data.id)
              ? true
              : false
          }
          onChange={(e) => handleCheckInput(e)}
        />
      </td>
      <td className="p-2 align-middle text-dark fw-medium">{data.palet_id}</td>
      <td className="p-2 align-middle">
        <BadgeStatusSj
          text={data.is_available ? 'available' : 'used'}
          bgColor={data.is_available ? 'success' : 'danger'}
        />
      </td>
      <td className="p-2 align-middle text-muted">{data.location}</td>
      <td className="p-2 align-middle text-end">
        <TableAction>
          <button
            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
            style={{ width: '32px', height: '32px' }}
            onClick={() => onHandleDownloadQr(dataValue)}
          >
            <QrCode size={13} />
          </button>
        </TableAction>
      </td>
    </tr>
  )
}

export default MasterPallete
